// Phase C — logged-OUT bilingual curriculum grid (Stage 4, build-only / parked).
//
// Reuses the /standards page card-grid shell. Data is a build-time static dataset
// (src/data/peCurriculum.ts) generated from the authoritative box DB, because the
// pe_* tables are NOT anon-readable (RLS) and there is no public chapters endpoint —
// a logged-out page therefore cannot fetch them live. Thumbnails load from the public
// Cloudflare CDN (https://cdn.startupsmartup.com/...), which is open.
//
// Thumbnail art is 1408x768 (11:6) with the chapter title baked into the top of the
// image, so images render with object-contain in an 11:6 box — never cropped.
//
// Logged-IN users get the inline chapter player (CurriculumChapterPlayer: tier +
// EN/ES switch, same pe_episodes → Vimeo resolve as /present). Logged-out users
// get the marketing detail only — pe_episodes is RLS-gated, so no video.

import { useMemo, useState } from "react";
import useUserStore from "../../stores/user";
import { useUiLangStore } from "../../stores/uiLang";
import CurriculumChapterPlayer from "./CurriculumChapterPlayer";
import {
  peCurriculum,
  thumbFor,
  type Lang,
  type PeChapter,
  type PeVocab,
} from "../../data/peCurriculum";
import { peSeasonLabel } from "../../data/peSeasons";

const SEASONS = [1, 2, 3, 4];
// Run E2 J3: canon season names (src/data/peSeasons.ts), one source for every page.
const SEASON_TITLES: Record<number, { en: string; es: string }> = Object.fromEntries(
  SEASONS.map((n) => [n, { en: peSeasonLabel(n, "en", " · "), es: peSeasonLabel(n, "es", " · ") }]),
);

const T = {
  en: {
    heading: "Project Explore Curriculum",
    sub: "Four seasons of bilingual financial-literacy stories for K-5. Browse every chapter.",
    chapter: "Chapter",
    plot: "Plot summary",
    fl: "Financial-literacy focus",
    vocab: "Key vocabulary",
    example: "Example",
    back: "← Back to chapters",
  },
  es: {
    heading: "Plan de Estudios de Project Explore",
    sub: "Cuatro temporadas de cuentos bilingües de educación financiera para K-5. Explora cada capítulo.",
    chapter: "Capítulo",
    plot: "Resumen de la trama",
    fl: "Enfoque de educación financiera",
    vocab: "Vocabulario clave",
    example: "Ejemplo",
    back: "← Volver a los capítulos",
  },
};

// Active-language string; silently fall back to EN when ES is absent (no user-visible
// badge — never invent Spanish, just show the English we have).
function pick(lang: Lang, en: string, es: string | null): string {
  if (lang === "es" && es != null && es !== "") return es;
  return en;
}

// Display-layer headword formatting (FIX B). NEVER render a raw slug:
//  - underscores -> spaces, always, both languages
//  - real lexeme words (ES headword) keep their source casing
//  - slug-only values (EN, or ES fallback) are title-cased, with small joining words
//    left lowercase ("supply_and_demand" -> "Supply and Demand")
// Does not mutate the underlying word/word_es keys used for lookups.
const SMALL_WORDS = new Set([
  "and", "or", "of", "the", "a", "an", "to", "for", "vs", "versus",
  "y", "de", "la", "el", "los", "las", "del", "con", "en", "o",
]);
function deUnderscore(s: string): string {
  return s.replace(/_/g, " ");
}
function titleCaseSlug(s: string): string {
  return deUnderscore(s)
    .split(" ")
    .filter(Boolean)
    .map((w, i) =>
      i > 0 && SMALL_WORDS.has(w.toLowerCase())
        ? w.toLowerCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(" ");
}
function headword(lang: Lang, v: PeVocab): string {
  // Real ES lexeme term: preserve source casing (Spanish common nouns stay lowercase).
  if (lang === "es" && v.word_es) return deUnderscore(v.word_es);
  // EN, or ES with no lexeme: format the slug for display.
  return titleCaseSlug(v.word);
}

export default function CurriculumPage() {
  const user = useUserStore((s) => s.user);
  const isLoggedIn = !!user;

  // Run E2 J2: the English / Español buttons set the shared UI language, so the
  // header and footer follow this page (and this page follows the header switch).
  const lang = useUiLangStore((s) => s.lang) as Lang;
  const setLang = useUiLangStore((s) => s.setLang);
  const [season, setSeason] = useState<number>(1);
  const [selected, setSelected] = useState<PeChapter | null>(null);
  const [playing, setPlaying] = useState<PeChapter | null>(null);

  const t = T[lang];
  const chapters = useMemo(
    () => peCurriculum.filter((c) => c.season === season).sort((a, b) => a.chapter - b.chapter),
    [season]
  );

  function onCardClick(ch: PeChapter) {
    if (isLoggedIn) {
      setSelected(null);
      setPlaying(ch);
    } else {
      setPlaying(null);
      setSelected(ch);
    }
  }

  return (
    // pt-28 clears the fixed site Header (~72-80px tall) so the page title is fully
    // visible at scroll-top. scroll-mt-28 keeps in-page anchors clear of the header too.
    <div className="max-w-6xl mx-auto px-6 pt-28 pb-16 scroll-mt-28">
      {/* Header + language toggle */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t.heading}</h1>
          <p className="text-gray-600 mt-2 max-w-2xl">{t.sub}</p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden self-start">
          {(["en", "es"] as Lang[]).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-4 py-2 text-sm font-semibold ${
                lang === l ? "bg-gray-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {l === "en" ? "English" : "Español"}
            </button>
          ))}
        </div>
      </div>

      {/* Season selector */}
      <div className="flex flex-wrap gap-2 mb-10">
        {SEASONS.map((s) => (
          <button
            key={s}
            onClick={() => {
              setSeason(s);
              setSelected(null);
              setPlaying(null);
            }}
            className={`px-4 py-2 rounded-full text-sm font-semibold border ${
              season === s
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {SEASON_TITLES[s][lang]}
          </button>
        ))}
      </div>

      {/* Logged-in: inline chapter player. key resets tier/lang per chapter. */}
      {playing && isLoggedIn && (
        <CurriculumChapterPlayer
          key={playing.slug}
          chapter={playing}
          initialLang={lang}
          onBack={() => setPlaying(null)}
        />
      )}

      {/* Detail (logged-out) */}
      {selected && !isLoggedIn && (
        <ChapterDetail chapter={selected} lang={lang} onBack={() => setSelected(null)} />
      )}

      {/* Grid */}
      {!selected && !(playing && isLoggedIn) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {chapters.map((ch) => {
            const img = thumbFor(ch, 1, lang);
            const title = pick(lang, ch.title, ch.title_es);
            return (
              <div
                key={ch.slug}
                onClick={() => onCardClick(ch)}
                className="feature-card rounded-lg shadow-lg bg-white overflow-hidden cursor-pointer transform transition hover:translate-y-[-5px]"
              >
                {/* 11:6 == 1408/768, the native thumbnail ratio; contain = never crop the baked-in title */}
                <div className="aspect-[11/6] bg-gray-100 flex items-center justify-center">
                  {img && (
                    <img
                      src={img}
                      alt={`${t.chapter} ${ch.chapter}: ${title}`}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                    {t.chapter} {ch.chapter}
                  </span>
                  {/* Run E2 J3: long titles (S2 Ch 12) wrap to 2 lines instead of being cut off;
                      min-h keeps every card's summary on the same line. */}
                  <h2
                    className="text-lg font-bold leading-snug text-gray-900 mt-1 line-clamp-2 min-h-[3.1rem] break-words"
                    title={title}
                  >
                    {title}
                  </h2>
                  <p className="text-gray-600 text-sm mt-2 line-clamp-3">
                    {pick(lang, ch.summary, ch.summary_es)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChapterDetail({
  chapter,
  lang,
  onBack,
}: {
  chapter: PeChapter;
  lang: Lang;
  onBack: () => void;
}) {
  const t = T[lang];
  const title = pick(lang, chapter.title, chapter.title_es);
  const summary = pick(lang, chapter.summary, chapter.summary_es);
  const fl = pick(lang, chapter.fl_pillar, chapter.fl_pillar_es);
  const hero = thumbFor(chapter, 1, lang);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden mb-10">
      {/* Full thumbnail, no crop (object-contain in native 11:6 box) */}
      {hero && (
        <div className="aspect-[11/6] bg-gray-100 flex items-center justify-center">
          <img src={hero} alt={title} className="w-full h-full object-contain" />
        </div>
      )}
      <div className="p-8">
        <button onClick={onBack} className="text-sm font-semibold text-indigo-700 hover:underline">
          {t.back}
        </button>
        <div className="mt-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
            {t.chapter} {chapter.chapter}
          </span>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>

        <section className="mt-6">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">{t.plot}</h3>
          <p className="text-gray-700 mt-1 leading-relaxed">{summary}</p>
        </section>

        <section className="mt-6">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">{t.fl}</h3>
          <p className="text-gray-700 mt-1">{fl}</p>
        </section>

        {chapter.vocab.length > 0 && (
          <section className="mt-6">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">{t.vocab}</h3>
            <ul className="mt-2 space-y-3">
              {chapter.vocab.map((v, i) => {
                const def = pick(lang, v.def_en, v.def_es);
                const exEn = v.ex_en ?? "";
                const ex = exEn || v.ex_es ? pick(lang, exEn, v.ex_es) : "";
                return (
                  <li key={i} className="border-l-2 border-indigo-200 pl-3">
                    <span className="font-semibold text-gray-900">{headword(lang, v)}</span>
                    <span className="text-gray-700">
                      {" — "}
                      {def}
                    </span>
                    {ex && (
                      <div className="text-gray-500 text-sm italic mt-0.5">
                        {t.example}: {ex}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {/* takeaways: pe_chapters.learning_objectives empty for all 48 -> section omitted (no source) */}
      </div>
    </div>
  );
}
